// Part groups (shared/part-groups.ts, migration 039): parts that are one thing in several colours or models. Here: the
// item panel's Group section (the members as a list or a grid, what's the same on every part, the shared files and
// Release for all…, the artwork for every part at once: GroupFiles.tsx), Make a group… (from the Parts library's
// selection), Add a part like this, the group's settings, and the choice a drop on a shared file's slot asks (all
// members, or only this part). The Parts library folds a group's
// rows under one (ItemsPage.tsx); the BOM editor's Fill from group is in BomFill.tsx.
import { Check, Copy, Files, LogOut, Plus, Rocket, Settings2, X } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { Item, PartGroup } from "../../../shared/db.gen.ts";
import {
  AXIS_SUGGESTIONS,
  axesText,
  axisValues,
  commonName,
  type FieldDiff,
  type GroupValues,
  groupDiffs,
  guessValues,
  isCurrent,
  knownAs,
  knownAsText,
  linkDiffs,
  MAX_AXES,
  type MemberFile,
  memberLabel,
  membersOf,
  proposeName,
  proposeSku,
  SAME_FIELDS,
  SHAREABLE_KINDS,
  SHARED_FILE_LABELS,
  type SharedFileState,
  sharedFileStates,
  sharedFileText,
  skuStem,
  valueProblems,
  valuesOf,
} from "../../../shared/part-groups.ts";
import { skuPrefix } from "../../../shared/part-sku.ts";
import {
  categoryLabel,
  categoryOptions,
  ITEM_TYPE_LABELS,
  ITEM_TYPES,
  type ItemType,
  PACK_CATEGORY_LABELS,
  typeLabel,
  typeOf,
  typePatch,
} from "../../../shared/part-type.ts";
import { PACK_PRINT_LABELS, type PartType } from "../../../shared/projects.ts";
import type { SpecFileKind } from "../../../shared/specs.ts";
import { hhlStock } from "../../../shared/stock.ts";
import { useBomStructure } from "../lib/boms.ts";
import { PROCUREMENT_LABELS, plural, qty } from "../lib/format.ts";
import { can, mutate, mutateWithResult } from "../lib/mutate.ts";
import { useUsedIn } from "../lib/projects.ts";
import { useRows } from "../lib/sync.ts";
import { Badge, Button, cx, Input, Select, Textarea } from "../ui/controls.tsx";
import { type Column, DataTable } from "../ui/DataTable.tsx";
import { DialogLayer } from "../ui/DialogLayer.tsx";
import { usePaneOpen } from "../ui/SidePane.tsx";
import { restOf, SkuInput } from "../ui/SkuInput.tsx";
import { toast } from "../ui/toast.tsx";
import { GroupFilesDialog, hasFiles, perPartNeed } from "./GroupFiles.tsx";
import { ReleaseAllDialog } from "./PartGroupRelease.tsx";
import { type SpecIndex, useSpecIndex } from "./specs/lib.ts";

/** Every group and its members (current ones first, by SKU), rebuilt when parts or groups change. */
export function usePartGroups() {
  const groups = useRows("part_group");
  const items = useRows("item");
  return useMemo(() => {
    const byId = new Map(groups.map((g) => [g.id, g]));
    const membersByGroup = new Map<string, Item[]>();
    for (const i of items) if (i.part_group_id) membersByGroup.set(i.part_group_id, [...(membersByGroup.get(i.part_group_id) ?? []), i]);
    for (const [id, list] of membersByGroup) membersByGroup.set(id, membersOf(list, id));
    return { groups, byId, membersByGroup };
  }, [groups, items]);
}

/** A part's draft upload of one kind of file and its released one (sha256), and the draft's file version. */
export function memberFile(ix: SpecIndex, itemId: string, kind: SpecFileKind): MemberFile & { versionId: string | null } {
  const spec = ix.specByItem.get(itemId);
  if (!spec) return { draft: null, released: null, versionId: null };
  const slot = (ix.filesBySpec.get(spec.id) ?? []).find((f) => f.in_draft && f.kind === kind && f.current_version_id);
  const v = slot?.current_version_id ? ix.versionById.get(slot.current_version_id) : undefined;
  const rel = spec.current_revision_id ? (ix.filesByRevision.get(spec.current_revision_id) ?? []).find((f) => f.kind === kind) : undefined;
  return { draft: v?.sha256 ?? null, released: rel?.sha256 ?? null, versionId: v?.id ?? null };
}

/** The shared files' state across a group, for the Group section and the Parts library's group rows. */
export function useSharedFiles(group: PartGroup | undefined, members: readonly Item[]): SharedFileState[] {
  const ix = useSpecIndex();
  return useMemo(() => (group ? sharedFileStates(group, members, (id, kind) => memberFile(ix, id, kind)) : []), [group, members, ix]);
}

/** The shared file kinds a Category usually shares: a printed box's or sleeve's die cut, a molded part's mould. */
export function defaultShared(item: Pick<Item, "part_type" | "pack_type">): SpecFileKind[] {
  if (item.part_type === "packaging") return ["box", "sleeve", "insert"].includes(item.pack_type) ? ["dieline"] : [];
  if (item.part_type === "molded" || item.part_type === "metal") return ["step"];
  return [];
}

// The Group section ------------------------------------------------------------------------------------

export function GroupSection({ item }: { item: Item }) {
  const { byId, membersByGroup } = usePartGroups();
  const group = item.part_group_id ? byId.get(item.part_group_id) : undefined;
  const members = (group && membersByGroup.get(group.id)) ?? [];
  const shared = useSharedFiles(group, members);
  const [adding, setAdding] = useState<GroupValues | null>(null);
  const [settings, setSettings] = useState(false);
  const [releasing, setReleasing] = useState(false);
  // Artwork for all --- the dialog, with what was dropped on the section (if it was)
  const [filing, setFiling] = useState<File[] | null>(null);
  const [over, setOver] = useState(false);
  if (!group) return null;

  const current = members.filter(isCurrent);
  const need = can("spec_file.create") && current.length > 1 ? perPartNeed(group, item) : null;
  // Add a part like this: this part's values, but the last axis (the colour, usually) is the new part's to say.
  const leave = async () => {
    const last = members.length === 1;
    if (
      !confirm(
        last
          ? `Take ${item.sku || item.name} out of ${group.name}? It's the last part, so the group goes.`
          : `Take ${item.sku || item.name} out of ${group.name}?`,
      )
    )
      return;
    if (await mutate("part_group.leave", { item_id: item.id })) toast.ok(`${item.sku || item.name} is in no group now`);
  };

  return (
    <section
      className={cx("mt-3 border-t border-line pt-2", over && "rounded-md bg-accent-soft outline-2 outline-accent outline-dashed")}
      onDragOver={(e) => {
        if (!need || !hasFiles(e)) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
      }}
      onDrop={(e) => {
        setOver(false);
        if (!need || !e.dataTransfer.files.length) return;
        e.preventDefault();
        setFiling([...e.dataTransfer.files]);
      }}
    >
      <div className="flex items-center gap-2 px-2 pb-1">
        <h3 className="shrink-0 text-[11px] font-medium tracking-wide text-faint uppercase">Group</h3>
        <span className="min-w-0 truncate font-medium" title={group.notes || group.name}>
          {group.name}
        </span>
        <span className="min-w-0 shrink-[2] truncate text-[12px] text-muted">
          {plural(current.length, "part")}
          {members.length > current.length && ` (+${members.length - current.length} old)`} · {axesText(group)}
        </span>
        {can("part_group.update") && (
          <button
            type="button"
            onClick={() => setSettings(true)}
            title="Group settings: name, what it varies by, shared files, type"
            className="shrink-0 rounded p-1 text-muted hover:bg-hover hover:text-text"
          >
            <Settings2 className="size-3.5" />
          </button>
        )}
      </div>
      <div className="px-2">
        <MemberGrid group={group} members={members} openId={item.id} onAdd={can("part_group.add_member") ? setAdding : undefined} />
        <SameOnAll group={group} members={members} item={item} />
        {shared.length > 0 && <SharedFiles group={group} members={members} item={item} states={shared} />}
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {can("part_group.add_member") && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Plus className="size-3.5" />}
              onClick={() => setAdding({ ...valuesOf(item), [group.varies_by[group.varies_by.length - 1]]: "" })}
              title="A new part like this one, in this group"
            >
              Add a part like this
            </Button>
          )}
          {need && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Files className="size-3.5" />}
              onClick={() => setFiling([])}
              title={`One PDF with a page for each part, or a file for each part (or drop them on this section): each goes in its part's draft`}
            >
              {need.label} for all…
            </Button>
          )}
          {can("change_notice.release") && current.length > 1 && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Rocket className="size-3.5" />}
              onClick={() => setReleasing(true)}
              title="One change notice releasing every part whose spec changed"
            >
              Release for all…
            </Button>
          )}
          {can("part_group.leave") && (
            <Button size="sm" variant="ghost" className="ml-auto" icon={<LogOut className="size-3.5" />} onClick={leave}>
              Leave group
            </Button>
          )}
        </div>
      </div>
      {adding && <AddPartDialog group={group} members={members} from={item} values={adding} onClose={() => setAdding(null)} />}
      {settings && <GroupSettingsDialog group={group} members={members} onClose={() => setSettings(false)} />}
      {releasing && <ReleaseAllDialog group={group} members={current} onClose={() => setReleasing(false)} />}
      {filing && need && (
        <GroupFilesDialog
          group={group}
          members={current}
          need={need}
          initial={filing}
          onClose={() => setFiling(null)}
          onRelease={() => {
            setFiling(null);
            setReleasing(true);
          }}
        />
      )}
    </section>
  );
}

/** Available at HHL per part (the Parts library's number). */
function useAvailable() {
  const levels = useRows("stock_level");
  const balances = useRows("stock_balance");
  const locations = useRows("location");
  return useMemo(() => hhlStock(levels, balances, locations), [levels, balances, locations]);
}

/** The members: a list for one axis (or three), in the usual table (views, columns, Copy / CSV); a grid for two (a pivot),
 * its fewer values across, where an empty cell offers a part. */
function MemberGrid({ group, members, openId, onAdd }: { group: PartGroup; members: Item[]; openId: string; onAdd?: (values: GroupValues) => void }) {
  const paneOpen = usePaneOpen(); // Side pane --- another member, in place of this one
  const stock = useAvailable();
  const usedIn = useUsedIn();
  const bix = useBomStructure();
  const values = axisValues(group, members);
  const known = (i: Item) => knownAsText(knownAs(bix, i.id));
  const tip = (i: Item) =>
    [
      `${i.sku} · ${i.name}`,
      `${qty(stock.get(i.id)?.available ?? 0)} available`,
      known(i) ? `Used for ${known(i)}` : usedIn.get(i.id) ? "In a BOM (not picked by an option)" : "Used by nothing",
    ].join("\n");
  const chips = (i: Item) => (
    <>
      {i.archived ? <Badge tone="warn">Archived</Badge> : !isCurrent(i) && <Badge tone="warn">{i.lifecycle === "eol" ? "End of life" : "Obsolete"}</Badge>}
      {i.lifecycle === "npi" && <Badge tone="accent">New</Badge>}
      {!usedIn.get(i.id) && isCurrent(i) && <Badge>used by nothing</Badge>}
    </>
  );
  const open = (i: Item) => paneOpen("item", i.id, true);

  if (group.varies_by.length === 2 && members.length) {
    // Columns: the axis with fewer values (Wireless | Wired); rows: the other (the colours).
    const across = values[0].length <= values[1].length ? 0 : 1;
    const down = 1 - across;
    const at = (row: string, col: string) =>
      members.filter((m) => {
        const v = valuesOf(m);
        return v[group.varies_by[down]]?.toLowerCase() === row.toLowerCase() && v[group.varies_by[across]]?.toLowerCase() === col.toLowerCase();
      });
    return (
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0.5 text-[12px]">
          <thead>
            <tr>
              <th className="w-1/3 px-1 text-left font-normal text-faint">{group.varies_by[down]}</th>
              {values[across].map((c) => (
                <th key={c} className="px-1 text-left font-medium text-muted">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {values[down].map((r) => (
              <tr key={r}>
                <td className="truncate px-1 text-muted" title={r}>
                  {r}
                </td>
                {values[across].map((c) => {
                  const here = at(r, c);
                  return (
                    <td key={c} className="align-top">
                      {here.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          title={tip(m)}
                          onClick={() => open(m)}
                          className={cx(
                            "flex w-full items-center gap-1 rounded border px-1.5 py-0.5 text-left",
                            m.id === openId ? "border-accent bg-selected" : "border-line hover:bg-hover",
                            !isCurrent(m) && "opacity-60",
                          )}
                        >
                          <span className={cx("min-w-0 flex-1 truncate font-mono", !isCurrent(m) && "line-through")}>{m.sku}</span>
                          {!usedIn.get(m.id) && isCurrent(m) && <span className="text-[10px] text-faint">unused</span>}
                          <span className="font-mono text-faint">{qty(stock.get(m.id)?.available ?? 0)}</span>
                        </button>
                      ))}
                      {!here.length &&
                        (onAdd ? (
                          <button
                            type="button"
                            title={`Add a ${c} ${r} part`}
                            onClick={() => onAdd({ [group.varies_by[down]]: r, [group.varies_by[across]]: c })}
                            className="flex w-full items-center justify-center rounded border border-dashed border-line py-0.5 text-faint hover:border-accent hover:text-accent-text"
                          >
                            <Plus className="size-3" />
                          </button>
                        ) : (
                          <span className="block px-1.5 text-faint">—</span>
                        ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // An old part (end of life, obsolete, archived) is faded, as it was
  const faded = (i: Item, className?: string) => cx(className, !isCurrent(i) && "opacity-60");
  const columns: Column<Item>[] = [
    {
      key: "values",
      header: group.varies_by.join(" × ") || "Values",
      width: "minmax(140px,1.6fr)",
      // Its place in the group (members come current first, by SKU), until another column is sorted
      value: (i) => members.indexOf(i),
      text: (i) => memberLabel(group, i),
      cell: (i) => (
        <span className={faded(i, "flex min-w-0 items-center gap-1.5")} title={tip(i)}>
          <span className="truncate">{memberLabel(group, i) || <span className="text-danger">no value</span>}</span>
          {chips(i)}
        </span>
      ),
    },
    {
      key: "used_for",
      header: "Used for",
      headerTitle: "What its BOM options pick it for",
      width: "minmax(120px,1.4fr)",
      value: (i) => known(i),
      cell: (i) => <span className={faded(i, "truncate text-[12px] text-faint")}>{known(i)}</span>,
    },
    {
      key: "sku",
      header: "SKU",
      width: "minmax(110px,1fr)",
      cell: (i) => <span className={faded(i, cx("truncate font-mono text-[12px] text-muted", !isCurrent(i) && "line-through"))}>{i.sku}</span>,
    },
    {
      key: "available",
      header: "Available",
      width: "80px",
      align: "right",
      value: (i) => stock.get(i.id)?.available ?? 0,
      cell: (i) => <span className={faded(i, "font-mono text-[12px] text-muted")}>{qty(stock.get(i.id)?.available ?? 0)}</span>,
    },
  ];
  return (
    <DataTable
      fit
      rows={members}
      columns={columns}
      defaultSort={{ key: "values" }}
      activeId={openId}
      onOpen={open}
      rowNoun={["part", "parts"]}
      exportName="Group parts"
      pageFilters={false}
    />
  );
}

const fieldOf = (i: Item, field: string) => (i as unknown as Record<string, unknown>)[field];

/** One field's value as people read it. */
function useValueText() {
  const vendors = useRows("vendor");
  return (field: string, value: unknown): string => {
    if (value == null || value === "") return "not set";
    if (field === "vendor_id") {
      const v = vendors.find((x) => x.id === value);
      return v ? v.short_name || v.name : "a vendor";
    }
    if (field === "procurement") return PROCUREMENT_LABELS[value as keyof typeof PROCUREMENT_LABELS] ?? String(value);
    if (field === "pack_type") return PACK_CATEGORY_LABELS[value as keyof typeof PACK_CATEGORY_LABELS] ?? String(value);
    if (field === "pack_print") return PACK_PRINT_LABELS[value as keyof typeof PACK_PRINT_LABELS] ?? String(value);
    if (field === "weight_g") return `${Number(value)} g`;
    if (field === "label_w_mm" || field === "label_h_mm") return `${Number(value)} mm`;
    if (field === "lead_time_days") return plural(Number(value), "day");
    if (field === "moq" || field === "order_multiple") return qty(value);
    return String(value);
  };
}

/** What every current part has alike, and where they differ, with "make all like this one". */
function SameOnAll({ group, members, item }: { group: PartGroup; members: Item[]; item: Item }) {
  const links = useRows("vendor_item");
  const text = useValueText();
  const diffs = useMemo(() => [...groupDiffs(members), ...linkDiffs(members, links)], [members, links]);
  const current = members.filter(isCurrent);
  if (current.length < 2) return null;
  const like = async (d: FieldDiff) => {
    const value = fieldOf(item, d.field);
    if (!confirm(`Make ${d.label.toLowerCase()} ${text(d.field, value)} on all ${members.length} parts of ${group.name}?`)) return;
    const { ok, result } = await mutateWithResult("part_group.set_all", { id: group.id, patch: { [d.field]: value } });
    if (ok) toast.ok(`${d.label} changed on ${plural((result as { changed: number }).changed, "part")}`);
  };
  const skus = (ids: string[]) => ids.map((id) => members.find((m) => m.id === id)?.sku).join(", ");
  const alike = SAME_FIELDS.filter(([f]) => !diffs.some((d) => d.field === f) && fieldOf(current[0], f) != null && fieldOf(current[0], f) !== "");
  // Its Type, and the Category its parts share (packaging's Category is one of the fields below: it may differ)
  const kind = [typeLabel(current[0]), typeOf(current[0]) === "packaging" ? "" : categoryLabel(current[0])].filter(Boolean).join(" · ");
  return (
    <div className="mt-1.5 rounded-md bg-surface-2/60 px-2.5 py-1.5 text-[12px]">
      <div className="text-muted">
        <span className="font-medium text-text">{diffs.length ? "Same on every part, except below" : "Same on every part"}</span>: {kind}
        {alike.map(([f, label]) => ` · ${label} ${text(f, fieldOf(current[0], f))}`).join("")}
        {!diffs.some((d) => d.field === "vendor_id") && " · one vendor"}
      </div>
      {diffs.map((d) => {
        const mine = d.values.find((v) => v.ids.includes(item.id));
        const settable = SAME_FIELDS.some(([f]) => f === d.field) && isCurrent(item) && can("part_group.set_all");
        return (
          <div key={d.field} className="mt-1 flex items-center gap-2">
            <span className="w-28 shrink-0 font-medium text-warn">{d.label} differs</span>
            <span className="min-w-0 flex-1 truncate">
              {d.values.map((v, n) => (
                <span key={String(v.value)} title={skus(v.ids)} className={cx(v === mine && "font-medium")}>
                  {n > 0 && " · "}
                  {text(d.field, v.value)} on {v.ids.length === current.length ? "all" : v.ids.length}
                </span>
              ))}
            </span>
            {settable && mine && (
              <Button
                size="sm"
                variant="ghost"
                icon={<Check className="size-3.5" />}
                onClick={() => like(d)}
                title={`Every part gets this part's ${d.label.toLowerCase()}`}
              >
                Make all like this one
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Each shared kind of file across the group, and copying this part's (or the group's) to the parts without it. */
function SharedFiles({ group, members, item, states }: { group: PartGroup; members: Item[]; item: Item; states: SharedFileState[] }) {
  const ix = useSpecIndex();
  const [busy, setBusy] = useState(false);
  const sku = (id: string) => members.find((m) => m.id === id)?.sku ?? "";
  const copy = async (kind: SpecFileKind, versionId: string, to: string[], what: string) => {
    setBusy(true);
    const { ok, result } = await mutateWithResult("spec_file.copy_to", { file_version_id: versionId, item_ids: to });
    setBusy(false);
    if (!ok) return;
    const r = result as { copied: unknown[]; unchanged: unknown[] };
    toast.ok(`${SHARED_FILE_LABELS[kind]} copied to ${plural(r.copied.length, "part")}${r.unchanged.length ? ` (${r.unchanged.length} had it)` : ""}: ${what}`);
  };
  return (
    <div className="mt-1.5 flex flex-col gap-1">
      {states.map((s) => {
        const mine = memberFile(ix, item.id, s.kind);
        const others = members.filter((m) => isCurrent(m) && m.id !== item.id);
        const behind = others.filter((m) => memberFile(ix, m.id, s.kind).draft !== mine.draft).map((m) => m.id);
        // The group's file, from a part that has it, for a part that doesn't.
        const holder = s.sha ? members.find((m) => memberFile(ix, m.id, s.kind).draft === s.sha) : undefined;
        const tone = s.same === s.total && s.released === s.total ? "ok" : s.same === s.total ? "accent" : "warn";
        return (
          <div key={s.kind} className="flex items-center gap-2 rounded-md border border-line px-2.5 py-1 text-[12px]">
            <span className={cx("size-2 shrink-0 rounded-full", { ok: "bg-ok", accent: "bg-accent", warn: "bg-warn" }[tone])} />
            <span
              className="min-w-0 flex-1 truncate"
              title={[
                s.missing.length ? `Missing: ${s.missing.map(sku).join(", ")}` : "",
                s.differs.length ? `Another file (their own cut): ${s.differs.map(sku).join(", ")}` : "",
              ]
                .filter(Boolean)
                .join("\n")}
            >
              {sharedFileText(s)}
            </span>
            {can("spec_file.copy_to") && mine.versionId && behind.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                icon={<Copy className="size-3.5" />}
                onClick={() => copy(s.kind, mine.versionId as string, behind, `${item.sku}'s`)}
                title={`Puts ${item.sku}'s ${s.label.toLowerCase()} in ${behind.map(sku).join(", ")} (their drafts; the bytes are stored once)`}
              >
                Copy to {behind.length === others.length ? `the other ${behind.length}` : behind.length}
              </Button>
            )}
            {can("spec_file.copy_to") && !mine.draft && holder && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                icon={<Copy className="size-3.5" />}
                onClick={() => copy(s.kind, memberFile(ix, holder.id, s.kind).versionId as string, [item.id], `${holder.sku}'s`)}
              >
                Copy the group's here
              </Button>
            )}
          </div>
        );
      })}
      <div className="px-1 text-[11px] text-faint">Each part's spec holds its own copy, so its POs, vendor page and releases work as before.</div>
    </div>
  );
}

// Dialogs ---------------------------------------------------------------------------------------------

export function Modal({
  title,
  width = 520,
  onClose,
  onSubmit,
  children,
  footer,
}: {
  title: ReactNode;
  width?: number;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  return createPortal(
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
        style={{ width }}
        className="max-w-[calc(100vw-32px)] rounded-xl border border-line bg-surface p-4 shadow-pop"
      >
        <div className="mb-3 text-[15px] font-semibold">{title}</div>
        {children}
        <div className="mt-4 flex items-center justify-end gap-2">{footer}</div>
      </form>
    </DialogLayer>,
    document.body,
  );
}

const Label = ({ text, children, hint }: { text: string; children: ReactNode; hint?: ReactNode }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[12px] text-muted">{text}</span>
    {children}
    {hint && <span className="text-[11px] text-faint">{hint}</span>}
  </label>
);

/** The axes a group varies by: a box each, up to three. */
function AxesInput({ axes, onChange }: { axes: string[]; onChange: (axes: string[]) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {axes.map((a, n) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a box stays in place while its axis is renamed
        <span key={n} className="flex items-center gap-1">
          {n > 0 && <span className="text-faint">×</span>}
          <Input value={a} list="part-group-axes" className="h-7 w-36" onChange={(e) => onChange(axes.map((x, i) => (i === n ? e.target.value : x)))} />
          {axes.length > 1 && (
            <button
              type="button"
              className="rounded p-0.5 text-faint hover:text-danger"
              title="Remove"
              onClick={() => onChange(axes.filter((_, i) => i !== n))}
            >
              <X className="size-3.5" />
            </button>
          )}
        </span>
      ))}
      {axes.length < MAX_AXES && (
        <Button
          size="sm"
          variant="ghost"
          icon={<Plus className="size-3.5" />}
          onClick={() => onChange([...axes, AXIS_SUGGESTIONS.find((s) => !axes.includes(s)) ?? ""])}
        >
          Another
        </Button>
      )}
      <datalist id="part-group-axes">
        {AXIS_SUGGESTIONS.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </div>
  );
}

function SharedInput({ value, onChange }: { value: SpecFileKind[]; onChange: (v: SpecFileKind[]) => void }) {
  return (
    <div className="flex flex-wrap gap-1">
      {SHAREABLE_KINDS.map((k) => {
        const on = value.includes(k);
        return (
          <button
            key={k}
            type="button"
            onClick={() => onChange(on ? value.filter((x) => x !== k) : [...value, k])}
            className={cx(
              "h-6 rounded border px-2 text-[12px]",
              on ? "border-accent bg-accent-soft text-accent-text" : "border-line text-muted hover:text-text",
            )}
          >
            {SHARED_FILE_LABELS[k]}
          </button>
        );
      })}
    </div>
  );
}

/** Make a group… from parts picked in the Parts library: its name, what it varies by and each part's values. */
export function MakeGroupDialog({ items: picked, onClose, onMade }: { items: Item[]; onClose: () => void; onMade: (id: string) => void }) {
  const [items] = useState(() => [...picked].sort((a, b) => a.sku.localeCompare(b.sku, undefined, { numeric: true })));
  const { byId } = usePartGroups();
  const [name, setName] = useState(() => commonName(items));
  const [axes, setAxes] = useState<string[]>(() => {
    const first = items.find((i) => i.part_group_id && byId.get(i.part_group_id));
    return first ? [...(byId.get(first.part_group_id as string)?.varies_by ?? ["Color"])] : ["Color"];
  });
  const [values, setValues] = useState(() => guessValues(items, axes));
  const [shared, setShared] = useState<SpecFileKind[]>(() => defaultShared(items[0]));
  const typePrefix = items[0] ? skuPrefix(items[0]) : ""; // Part SKUs --- the parts' type's, fixed in front
  const [prefix, setPrefix] = useState(() => restOf(skuStem({ sku_prefix: "" }, items), typePrefix));
  const [busy, setBusy] = useState(false);

  const setAxesKeep = (next: string[]) => {
    // Values follow their axis when it's renamed in place; a new axis starts empty.
    setValues((was) => Object.fromEntries(items.map((i) => [i.id, Object.fromEntries(next.map((a, n) => [a, was[i.id]?.[a] ?? was[i.id]?.[axes[n]] ?? ""]))])));
    setAxes(next);
  };
  const clean = axes.map((a) => a.trim()).filter(Boolean);
  const simulated = items.map((i) => ({ ...i, group_values: values[i.id] ?? {} }));
  const problems = [
    !name.trim() && "Name the group.",
    clean.length !== axes.length && "Name each thing it varies by.",
    // A group's parts share one Type, and one Category unless they're packaging (kind and part_type are locked)
    new Set(items.map(typeOf)).size > 1
      ? `These are ${[...new Set(items.map(typeLabel))].join(" and ")}: a group's parts share one Type. Set it first.`
      : new Set(items.map((i) => i.part_type)).size > 1
        ? `These are ${[...new Set(items.map(categoryLabel))].join(" and ")}: a group's parts share one Category (unless they're packaging). Set it first.`
        : new Set(items.map((i) => i.kind)).size > 1 && "A group's parts share one Type: set it again on the ones that differ.",
    ...valueProblems({ name, varies_by: axes }, simulated),
  ].filter((p): p is string => !!p);
  const moving = items.filter((i) => i.part_group_id && byId.get(i.part_group_id));

  const submit = async () => {
    if (problems.length || busy) return;
    setBusy(true);
    const id = crypto.randomUUID();
    const { ok } = await mutateWithResult("part_group.create", {
      id,
      name: name.trim(),
      sku_prefix: prefix.trim() ? typePrefix + prefix.trim() : "",
      varies_by: clean,
      shared_files: shared,
      members: items.map((i) => ({ item_id: i.id, values: values[i.id] ?? {} })),
      move: moving.length > 0,
    });
    setBusy(false);
    if (!ok) return;
    toast.ok(`${name.trim()}: ${plural(items.length, "part")} grouped`);
    onMade(id);
  };

  return (
    <Modal
      title={`Make a group of ${plural(items.length, "part")}`}
      width={640}
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={busy || problems.length > 0} kbd="↵">
            Make the group
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Label text="Name">
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. GCU2 box sleeve" />
          </Label>
          <Label text="SKUs start with" hint="For a new part's SKU">
            <SkuInput prefix={typePrefix} value={prefix} onChange={setPrefix} placeholder="e.g. GCU2-SLV-" />
          </Label>
        </div>
        <Label text="The parts differ by" hint="In the order the grid shows them. Say what the part is: a cap's resin colour, a sleeve's colourway.">
          <AxesInput axes={axes} onChange={setAxesKeep} />
        </Label>
        <div className="max-h-[40vh] overflow-y-auto rounded-md border border-line">
          <table className="w-full text-[12px]">
            <thead className="sticky top-0 bg-surface-2 text-muted">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Part</th>
                {axes.map((a, n) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: a box stays in place while its axis is renamed
                  <th key={n} className="px-2 py-1 text-left font-medium">
                    {a || "…"}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-line">
                  <td className="px-2 py-1">
                    <div className="font-mono">{i.sku}</div>
                    <div className="max-w-56 truncate text-faint" title={i.name}>
                      {i.name}
                    </div>
                  </td>
                  {axes.map((a, n) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: a box stays in place while its axis is renamed
                    <td key={n} className="px-1 py-1">
                      <Input
                        value={values[i.id]?.[a] ?? ""}
                        className="h-7"
                        onChange={(e) => setValues((was) => ({ ...was, [i.id]: { ...was[i.id], [a]: e.target.value } }))}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Label
          text="Files every part shares"
          hint="Dropping one on a part's slot can put it in every part's spec: the die cut for printed packaging, the STEP for a mould."
        >
          <SharedInput value={shared} onChange={setShared} />
        </Label>
        {moving.length > 0 && (
          <div className="rounded-md bg-accent-soft/60 px-2.5 py-1.5 text-[12px]">
            {moving.map((i) => `${i.sku} leaves ${byId.get(i.part_group_id as string)?.name}`).join("; ")}.
          </div>
        )}
        {problems.map((p) => (
          <div key={p} className="text-[12px] text-danger">
            {p}
          </div>
        ))}
      </div>
    </Modal>
  );
}

/** Add a part like this: a sibling's fields, vendors (not their SKU), spec text and shared files, under new values. */
function AddPartDialog({
  group,
  members,
  from,
  values: initial,
  onClose,
}: {
  group: PartGroup;
  members: Item[];
  from: Item;
  values: GroupValues;
  onClose: () => void;
}) {
  const paneOpen = usePaneOpen(); // Side pane --- the new part, in place of the one it was made like
  const items = useRows("item");
  const links = useRows("vendor_item");
  const ix = useSpecIndex();
  const [likeId, setLikeId] = useState(isCurrent(from) ? from.id : (members.find(isCurrent)?.id ?? from.id));
  const like = members.find((m) => m.id === likeId) ?? from;
  const [values, setValues] = useState<GroupValues>(() => Object.fromEntries(group.varies_by.map((a) => [a, initial[a] ?? ""])));
  const taken = useMemo(() => new Set(items.filter((i) => !i.archived).map((i) => i.sku.toUpperCase())), [items]);
  // Part SKUs --- its type's prefix is fixed in front and the box holds the rest (a group from before the prefixes has
  // none in its stem)
  const typePrefix = skuPrefix(like);
  const proposed = {
    rest: restOf(
      proposeSku(group, members, values, (s) => taken.has(s.toUpperCase())),
      typePrefix,
    ),
    name: proposeName(group, like, values),
  };
  const [rest, setRest] = useState<string | null>(null);
  const sku = typePrefix + (rest ?? proposed.rest).trim();
  const [name, setName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const problems = valueProblems(group, [...members, { ...like, id: "new", sku: sku, archived: false, lifecycle: "npi", group_values: values }]).filter((p) =>
    p.includes(sku),
  );
  const copied = members.length && ix.specByItem.get(like.id) ? group.shared_files.filter((k) => memberFile(ix, like.id, k as SpecFileKind).draft) : [];
  const nLinks = links.filter((l) => l.item_id === like.id).length;

  const submit = async () => {
    if (busy || problems.length || group.varies_by.some((a) => !values[a]?.trim())) return;
    setBusy(true);
    const id = crypto.randomUUID();
    const { ok } = await mutateWithResult("part_group.add_member", {
      id,
      from_item_id: like.id,
      sku: sku,
      name: name ?? proposed.name,
      values,
    });
    setBusy(false);
    if (!ok) return;
    toast.ok(`${sku} added to ${group.name}`);
    onClose();
    paneOpen("item", id, true);
  };

  return (
    <Modal
      title={`Add a part to ${group.name}`}
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={busy || problems.length > 0 || group.varies_by.some((a) => !values[a]?.trim())} kbd="↵">
            Add the part
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          {group.varies_by.map((a, n) => (
            <Label key={a} text={a}>
              <Input
                autoFocus={n === group.varies_by.length - 1}
                value={values[a] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [a]: e.target.value }))}
                list={`axis-${n}`}
              />
              <datalist id={`axis-${n}`}>
                {axisValues(group, members)[n].map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </Label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Label text="SKU">
            <SkuInput prefix={typePrefix} value={rest ?? proposed.rest} onChange={setRest} />
          </Label>
          <Label text="Like">
            <Select
              value={likeId}
              onChange={(e) => setLikeId(e.target.value)}
              options={members.filter(isCurrent).map((m) => ({ value: m.id, label: `${m.sku} · ${memberLabel(group, m)}` }))}
            />
          </Label>
        </div>
        <Label text="Name">
          <Input value={name ?? proposed.name} onChange={(e) => setName(e.target.value)} />
        </Label>
        <div className="rounded-md bg-surface-2/60 px-2.5 py-1.5 text-[12px] text-muted">
          From {like.sku}: its Type, Category{typeOf(like) === "packaging" ? ", printing" : ""}, unit, procurement, HS code, country, weight and tags;{" "}
          {nLinks ? `its ${plural(nLinks, "vendor")} on the same terms (their SKU and name for it left blank)` : "no vendors (it has none)"}
          {"; its spec text"}
          {copied.length ? ` and its ${copied.map((k) => SHARED_FILE_LABELS[k as SpecFileKind]).join(" and ")} (the same file)` : ""}. Never its artwork,
          barcode, photo, cost, stock or plan. It starts as New (NPI).
        </div>
        {problems.map((p) => (
          <div key={p} className="text-[12px] text-danger">
            {p}
          </div>
        ))}
      </div>
    </Modal>
  );
}

/** A group's name, prefix, axes (renamed in place), shared files and notes; its parts' Type and Category (not for packaging,
 * whose parts may differ in Category); dissolving it. */
function GroupSettingsDialog({ group, members, onClose }: { group: PartGroup; members: Item[]; onClose: () => void }) {
  const [name, setName] = useState(group.name);
  const [prefix, setPrefix] = useState(() => restOf(group.sku_prefix, members[0] ? skuPrefix(members[0]) : ""));
  const [axes, setAxes] = useState([...group.varies_by]);
  const [shared, setShared] = useState(group.shared_files as SpecFileKind[]);
  const [notes, setNotes] = useState(group.notes);
  const first = members[0] ?? { kind: "material", part_type: "other" as PartType, pack_type: "" };
  const was = typeOf(first);
  const [type, setType] = useState<ItemType>(was === "sell" ? "material" : was);
  // A material's or sub-assembly's Category (part_type); packaging's isn't shared, and a service has none
  const [category, setCategory] = useState<PartType>(first.part_type && first.part_type !== "packaging" ? first.part_type : "other");
  const categories = type === "material" || type === "subassembly" ? categoryOptions(type) : [];
  const typed = { ...typePatch(first, type), ...(categories.length ? { part_type: category } : {}) };
  const [busy, setBusy] = useState(false);
  const renamed = axes.length === group.varies_by.length && axes.some((a, n) => a.trim() !== group.varies_by[n]);
  const typePrefix = skuPrefix(typed); // Part SKUs --- follows the Type set here

  const save = async () => {
    if (busy) return;
    if (axes.some((a) => !a.trim())) return toast.error("Name each thing it varies by");
    setBusy(true);
    let ok = await mutate("part_group.update", {
      id: group.id,
      patch: { name, sku_prefix: prefix.trim() ? typePrefix + prefix.trim() : "", shared_files: shared, notes },
    });
    if (ok && renamed) {
      const next = axes.map((a) => a.trim());
      ok = await mutate("part_group.set_members", {
        id: group.id,
        varies_by: next,
        members: members.map((m) => ({ item_id: m.id, values: Object.fromEntries(next.map((a, n) => [a, valuesOf(m)[group.varies_by[n]] ?? ""])) })),
      });
    }
    // Only what was changed here (a packaging group's parts keep their kind unless their Type changes)
    if (ok && members[0] && (type !== was || (categories.length > 0 && category !== first.part_type))) {
      const to = [ITEM_TYPE_LABELS[type], categories.find((o) => o.value === category)?.label].filter(Boolean).join(" · ");
      if (confirm(`Change all ${members.length} parts to ${to}?`)) ok = await mutate("part_group.set_all", { id: group.id, patch: typed });
    }
    setBusy(false);
    if (ok) onClose();
  };
  const dissolve = async () => {
    if (!confirm(`Dissolve ${group.name}? Its ${plural(members.length, "part")} stay as they are, each in no group.`)) return;
    if (await mutate("part_group.remove", { id: group.id })) {
      toast.ok(`${group.name} dissolved`);
      onClose();
    }
  };

  return (
    <Modal
      title={`${group.name} settings`}
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          {can("part_group.remove") && (
            <Button variant="danger" className="mr-auto" onClick={dissolve}>
              Dissolve group
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={busy} kbd="↵">
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Label text="Name">
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          </Label>
          <Label text="SKUs start with">
            <SkuInput prefix={typePrefix} value={prefix} onChange={setPrefix} />
          </Label>
        </div>
        <Label
          text="The parts differ by"
          hint={
            axes.length === group.varies_by.length ? "Renaming keeps each part's values" : "Each part needs a value for a new one: set them in Make a group…"
          }
        >
          <AxesInput axes={axes} onChange={(a) => setAxes(a.length > group.varies_by.length ? axes : a.slice(0, group.varies_by.length))} />
        </Label>
        <Label text="Files every part shares">
          <SharedInput value={shared} onChange={setShared} />
        </Label>
        <div className="grid grid-cols-2 gap-3">
          <Label text="Type (every part)">
            <Select
              value={type}
              onChange={(e) => setType(e.target.value as ItemType)}
              options={ITEM_TYPES.map((t) => ({ value: t, label: ITEM_TYPE_LABELS[t] }))}
            />
          </Label>
          {categories.length > 0 && (
            <Label text="Category (every part)">
              <Select value={category} onChange={(e) => setCategory(e.target.value as PartType)} options={categories} />
            </Label>
          )}
        </div>
        <Label text="Notes">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </Label>
      </div>
    </Modal>
  );
}

/** Asked when a file is dropped on a shared kind's slot: every current part of the group (default), or only this one. */
export function ShareChoice({
  group,
  count,
  label,
  file,
  onPick,
}: {
  group: PartGroup;
  count: number;
  label: string;
  file: string;
  onPick: (all: boolean | null) => void;
}) {
  return (
    <Modal
      title={`${label} for every part in ${group.name}?`}
      width={460}
      onClose={() => onPick(null)}
      onSubmit={() => onPick(true)}
      footer={
        <>
          <Button variant="ghost" onClick={() => onPick(null)}>
            Cancel
          </Button>
          <Button onClick={() => onPick(false)}>Only this part</Button>
          <Button variant="primary" type="submit" autoFocus kbd="↵">
            All {count}
          </Button>
        </>
      }
    >
      <div className="text-muted">
        {file} becomes the {label.toLowerCase()} in the draft spec of all {count} parts of {group.name} (stored once). Each part still releases its own
        revision.
      </div>
    </Modal>
  );
}
